import { useState, useEffect } from "react";
import { useUserSettings } from "../../contexts/UserSettingsContext";
import { BaseModal } from "./BaseModal";
import { AddMealToPlanModalSearchBar } from "./AddMealToPlanModalComponents/AddMealToPlanModalSearchBar";
import { AddMealToPlanModalRecipeList } from "./AddMealToPlanModalComponents/AddMealToPlanModalRecipeList";
import { AddMealToPlanModalFooter } from "./AddMealToPlanModalComponents/AddMealToPlanModalFooter";

export function AddMealToPlanModal({
  isOpen,
  onClose,
  dayName,
  mealType,
  onAddMeal,
  availableRecipes,
  initiallySelectedRecipes,
  currentDayCode,
  currentMealCode,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  // mismatchState tracks per-recipe resolution for serving-size mismatches.
  // Shape: { [mealId]: { resolved: boolean } }
  const [mismatchState, setMismatchState] = useState({});
  const { householdSize } = useUserSettings();

  const [updateMealPlanRequest, setUpdateMealPlanRequest] = useState({
    updatedMeals: [],
    leftoverMeals: [],
  });

  useEffect(() => {
    const recipes = initiallySelectedRecipes || [];
    setSelectedRecipes(recipes);
    setUpdateMealPlanRequest({ updatedMeals: recipes, leftoverMeals: [] });
  }, [initiallySelectedRecipes]);

  if (!availableRecipes) return null;

  const hasMismatch = (recipe) =>
    householdSize && recipe.servings !== householdSize;

  const filteredRecipes = availableRecipes.filter((recipe) =>
    recipe.mealName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const hasUnresolved = Object.values(mismatchState).some((s) => !s.resolved);
  const hasSelection = selectedRecipes.length > 0;

  const handleSelectRecipe = (recipe) => {
    const exists = selectedRecipes.some((r) => r.mealId === recipe.mealId);

    if (exists) {
      setSelectedRecipes((prev) => prev.filter((r) => r.mealId !== recipe.mealId));
      setUpdateMealPlanRequest((prev) => ({
        ...prev,
        updatedMeals: prev.updatedMeals.filter((m) => m.mealId !== recipe.mealId),
        leftoverMeals: prev.leftoverMeals.filter((m) => m.mealId !== recipe.mealId),
      }));
      setMismatchState((prev) => {
        const next = { ...prev };
        delete next[recipe.mealId];
        return next;
      });
      return;
    }

    if (hasMismatch(recipe)) {
      setSelectedRecipes((prev) => [...prev, recipe]);
      setMismatchState((prev) => ({
        ...prev,
        [recipe.mealId]: { resolved: false },
      }));
      return;
    }

    recipe.ingredientMultiplier = 1;
    setSelectedRecipes((prev) => [...prev, recipe]);
    setUpdateMealPlanRequest((prev) => ({
      ...prev,
      updatedMeals: [...prev.updatedMeals, recipe],
    }));
  };

  const handleResolveMismatch = (recipe, mode, leftoverInfo) => {
    if (mode === "leftover") {
      recipe.ingredientMultiplier = 1;
      setUpdateMealPlanRequest((prev) => ({
        updatedMeals: [...prev.updatedMeals, recipe],
        leftoverMeals: [
          ...prev.leftoverMeals,
          { mealId: recipe.mealId, dayCode: leftoverInfo.day, mealCode: leftoverInfo.meal },
        ],
      }));
    } else if (mode === "double") {
      recipe.ingredientMultiplier = 2;
      setUpdateMealPlanRequest((prev) => ({
        ...prev,
        updatedMeals: [...prev.updatedMeals, recipe],
      }));
    } else {
      recipe.ingredientMultiplier = 1;
      setUpdateMealPlanRequest((prev) => ({
        ...prev,
        updatedMeals: [...prev.updatedMeals, recipe],
      }));
    }

    setMismatchState((prev) => ({
      ...prev,
      [recipe.mealId]: { resolved: true },
    }));
  };

  const getMealDisplayInfo = (recipe) => {
    const willAddLeftover =
      updateMealPlanRequest.leftoverMeals.find((m) => m.mealId === recipe.mealId) !== undefined;
    const multiplier =
      updateMealPlanRequest.updatedMeals.find((m) => m.mealId === recipe.mealId)
        ?.ingredientMultiplier || 1;

    if (willAddLeftover) return { badge: "+ Leftover", color: "blue" };
    if (multiplier === 2) return { badge: "2x Portions", color: "green" };
    return null;
  };

  const handleAddMeal = () => {
    onAddMeal(updateMealPlanRequest);
    handleClose();
  };

  const handleClose = () => {
    setSelectedRecipes([]);
    setMismatchState({});
    setSearchQuery("");
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Meal"
      subtitle={`${dayName} • ${mealType}`}
      size="lg"
      maxHeight
      noPadding
    >
      <AddMealToPlanModalSearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      <AddMealToPlanModalRecipeList
        filteredRecipes={filteredRecipes}
        selectedRecipes={selectedRecipes}
        handleSelectRecipe={handleSelectRecipe}
        getMealDisplayInfo={getMealDisplayInfo}
        mismatchState={mismatchState}
        onResolveMismatch={handleResolveMismatch}
        householdSize={householdSize}
        currentDayCode={currentDayCode}
        currentMealCode={currentMealCode}
      />
      <AddMealToPlanModalFooter
        handleClose={handleClose}
        handleAddMeal={handleAddMeal}
        disabled={!hasSelection || hasUnresolved}
      />
    </BaseModal>
  );
}
